import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import CssBaseline from "@material-ui/core/CssBaseline";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Link from "@material-ui/core/Link";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChatIcon from "@material-ui/icons/Chat";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import WhitelabelPoweredBy from "../../components/WhitelabelPoweredBy";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    boxSizing: "border-box",
    backgroundColor:
      theme.palette.type === "light" ? "#f4f6fb" : theme.palette.background.default
  },
  cardWrap: {
    width: "100%",
    maxWidth: 420,
    margin: "0 auto"
  },
  paper: {
    width: "100%",
    maxWidth: 420,
    boxSizing: "border-box",
    backgroundColor:
      theme.palette.type === "light"
        ? "rgba(255, 255, 255, 0.95)"
        : theme.palette.background.paper,
    borderRadius: 20,
    padding: "36px 28px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.10)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    [theme.breakpoints.down("xs")]: {
      padding: "28px 20px",
      borderRadius: 16
    }
  },
  logoImg: {
    display: "block",
    width: "100%",
    maxWidth: 220,
    maxHeight: 70,
    objectFit: "contain",
    margin: "0 auto 20px auto",
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")"
  },
  tabs: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 6,
    backgroundColor:
      theme.palette.type === "light" ? "#f1f3f9" : "rgba(255,255,255,0.06)",
    borderRadius: 12,
    padding: 4,
    marginBottom: theme.spacing(2.5)
  },
  tab: {
    textTransform: "none",
    borderRadius: 9,
    fontWeight: 600,
    minHeight: 40,
    boxShadow: "none"
  },
  title: {
    fontWeight: 700,
    marginBottom: 4,
    fontSize: "1.15rem"
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2.5),
    fontSize: "0.875rem",
    lineHeight: 1.45
  },
  field: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": { borderRadius: 12 }
  },
  submit: {
    marginTop: theme.spacing(0.5),
    borderRadius: 12,
    padding: "12px 0",
    fontSize: 16,
    fontWeight: 600,
    textTransform: "none"
  },
  footer: {
    marginTop: theme.spacing(2.5),
    textAlign: "center"
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontWeight: 600,
    cursor: "pointer"
  },
  codeField: {
    "& input": {
      textAlign: "center",
      letterSpacing: 10,
      fontSize: 22,
      fontWeight: 600
    }
  },
  successIcon: {
    fontSize: 56,
    color: theme.palette.success.main,
    display: "block",
    margin: "0 auto 16px auto"
  },
  center: { textAlign: "center" }
}));

const ForgotPassword = () => {
  const classes = useStyles();
  const history = useHistory();

  const [options, setOptions] = useState({
    enabled: true,
    channels: ["email"],
    defaultChannel: "email",
    resendSeconds: 60
  });
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [step, setStep] = useState("request");
  const [channel, setChannel] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [resetId, setResetId] = useState(null);
  const [destination, setDestination] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;

    openApi
      .get("/auth/password-reset/options")
      .then(({ data }) => {
        if (!active) return;
        setOptions(data);
        if (data?.defaultChannel) {
          setChannel(data.defaultChannel);
        } else if (data?.channels?.length) {
          setChannel(data.channels[0]);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoadingOptions(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const requestCode = useCallback(
    async (silent = false) => {
      if (!email.trim()) {
        toast.error("Informe o e-mail da sua conta");
        return;
      }

      setSubmitting(true);
      try {
        const { data } = await openApi.post("/auth/password-reset/request", {
          email: email.trim(),
          channel
        });

        setResetId(data.resetId);
        setDestination(data.destination);
        setCooldown(data.resendSeconds || 60);
        setStep("confirm");

        if (silent) {
          toast.success("Novo código enviado");
        }
      } catch (err) {
        toastError(err);
      } finally {
        setSubmitting(false);
      }
    },
    [channel, email]
  );

  const handleReset = async (e) => {
    e.preventDefault();

    if (password.length < 5) {
      toast.error("A nova senha deve ter ao menos 5 caracteres");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("As senhas não conferem");
      return;
    }

    setSubmitting(true);
    try {
      await openApi.post("/auth/password-reset/confirm", {
        resetId,
        code: code.trim(),
        password
      });
      setStep("done");
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const channelUnavailable = !loadingOptions && options.enabled === false;

  const renderChannelTabs = () => {
    const available = options.channels || [];
    if (available.length < 2) return null;

    return (
      <div className={classes.tabs}>
        <Button
          className={classes.tab}
          variant={channel === "email" ? "contained" : "text"}
          color={channel === "email" ? "primary" : "default"}
          startIcon={<MailOutlineIcon />}
          onClick={() => setChannel("email")}
        >
          E-mail
        </Button>
        <Button
          className={classes.tab}
          variant={channel === "whatsapp" ? "contained" : "text"}
          color={channel === "whatsapp" ? "primary" : "default"}
          startIcon={<ChatIcon />}
          onClick={() => setChannel("whatsapp")}
        >
          WhatsApp
        </Button>
      </div>
    );
  };

  const renderRequestStep = () => (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        requestCode();
      }}
    >
      {renderChannelTabs()}

      <Typography variant="h6" className={classes.title}>
        {channel === "whatsapp" ? "Recuperar via WhatsApp" : "Recuperar via e-mail"}
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        {channel === "whatsapp"
          ? "Informe o e-mail da sua conta. Enviaremos um código para o WhatsApp cadastrado nela."
          : "Informe o e-mail da sua conta para receber um código de recuperação."}
      </Typography>

      <TextField
        fullWidth
        variant="outlined"
        label="E-mail"
        type="email"
        placeholder="email@exemplo.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={classes.field}
        autoFocus
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        className={classes.submit}
        disabled={submitting}
      >
        {submitting ? <CircularProgress size={24} /> : "Enviar código"}
      </Button>
    </form>
  );

  const renderConfirmStep = () => (
    <form onSubmit={handleReset}>
      <Typography variant="h6" className={classes.title}>
        Defina a nova senha
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Enviamos um código para <strong>{destination}</strong>. Digite-o abaixo junto
        com a nova senha.
      </Typography>

      <TextField
        fullWidth
        variant="outlined"
        label="Código de recuperação"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        inputProps={{ inputMode: "numeric", maxLength: 6 }}
        className={`${classes.field} ${classes.codeField}`}
        autoFocus
      />

      <TextField
        fullWidth
        variant="outlined"
        label="Nova senha"
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className={classes.field}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword((prev) => !prev)}
                edge="end"
                aria-label="alternar visibilidade da senha"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <TextField
        fullWidth
        variant="outlined"
        label="Confirmar nova senha"
        type={showPassword ? "text" : "password"}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        className={classes.field}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        className={classes.submit}
        disabled={submitting || code.length < 6}
      >
        {submitting ? <CircularProgress size={24} /> : "Redefinir senha"}
      </Button>

      <div className={classes.footer}>
        {cooldown > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Reenviar código em {cooldown}s
          </Typography>
        ) : (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => requestCode(true)}
          >
            Reenviar código
          </Link>
        )}
      </div>
    </form>
  );

  const renderDoneStep = () => (
    <div className={classes.center}>
      <CheckCircleIcon className={classes.successIcon} />
      <Typography variant="h6" className={classes.title}>
        Senha redefinida
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Sua senha foi alterada e as sessões antigas foram encerradas. Entre novamente
        com a nova senha.
      </Typography>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        className={classes.submit}
        onClick={() => history.push("/login")}
      >
        Ir para o login
      </Button>
    </div>
  );

  return (
    <div className={classes.root}>
      <CssBaseline />
      <div className={classes.cardWrap}>
        <div className={classes.paper}>
          <img className={classes.logoImg} alt="logo" />

          {loadingOptions && (
            <div className={classes.center}>
              <CircularProgress />
            </div>
          )}

          {!loadingOptions && channelUnavailable && (
            <div className={classes.center}>
              <Typography variant="h6" className={classes.title}>
                Recuperação indisponível
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Nenhum canal de envio está configurado. Entre em contato com o
                suporte para redefinir sua senha.
              </Typography>
            </div>
          )}

          {!loadingOptions && !channelUnavailable && (
            <>
              {step === "request" && renderRequestStep()}
              {step === "confirm" && renderConfirmStep()}
              {step === "done" && renderDoneStep()}
            </>
          )}

          {step !== "done" && (
            <div className={classes.footer}>
              <Link
                component={RouterLink}
                to="/login"
                variant="body2"
                className={classes.backLink}
              >
                <ArrowBackIcon fontSize="small" />
                Voltar para o login
              </Link>
            </div>
          )}
        </div>

        <div className={classes.footer}>
          <WhitelabelPoweredBy inline />
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
